import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';

import { AuthLayout } from '../../components/layout/AuthLayout';
import { Stack } from '../../components/layout/Stack';

export const Route = createFileRoute('/_public/terms')({
  component: TermsPage,
});

function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold uppercase tracking-wider text-text-secondary border-b border-border pb-1.5">
        {title}
      </h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function LegalItem({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-50 border border-accent-100 flex items-center justify-center text-caption font-bold text-accent-500">
        {index}
      </div>
      <div>
        <h5 className="text-xs font-bold text-text-primary">{title}</h5>
        <p className="text-caption text-text-secondary mt-0.5">{children}</p>
      </div>
    </div>
  );
}

function LegalBullet({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0 mt-1.5" />
      <p className="text-caption text-text-secondary leading-relaxed">{children}</p>
    </div>
  );
}

function TermsPage() {
  usePageTitle('Terms of Service');

  return (
    <AuthLayout
      title="Terms of Service"
      subtitle="Terms governing your use of the Kraphene mobile apps."
      cardClassName="max-w-[540px]"
    >
      <Stack gap="6" className="w-full">
        <p className="text-caption text-text-secondary leading-relaxed">
          These Terms of Service govern your use of the Kraphene mobile apps. The apps are provided
          to you by the company you belong to (your service provider), which operates your account
          and chit schemes using the Kraphene platform.
        </p>

        <LegalSection title="Acceptance">
          <p className="text-caption text-text-secondary leading-relaxed">
            By downloading, installing, or using the apps, you agree to these Terms and to the
            Privacy Policy. You confirm that you are entitled to accept them. If you do not agree,
            do not use the apps.
          </p>
        </LegalSection>

        <LegalSection title="Your account">
          <LegalItem index={1} title="MPIN confidentiality">
            You must keep your MPIN secret and never share it with anyone, including staff of the
            company you belong to. You are responsible for activity carried out using your MPIN.
          </LegalItem>
          <LegalItem index={2} title="Device binding">
            The apps are bound to a registered device. You must not sign in from an unregistered
            device, and you must not let anyone else use your registered device to access your
            account.
          </LegalItem>
          <LegalItem index={3} title="Supervisory approval where applicable">
            Where applicable, actions you record through the agent app require supervisory approval
            by the company you belong to before they take effect.
          </LegalItem>
        </LegalSection>

        <LegalSection title="Acceptable use">
          <LegalBullet>
            Use the apps only for lawful purposes and in accordance with the rules of your chit
            scheme.
          </LegalBullet>
          <LegalBullet>
            Do not attempt to gain unauthorised access to the apps, to other accounts, or to the
            platform.
          </LegalBullet>
          <LegalBullet>
            Do not interfere with the apps, reverse engineer them, or submit false, misleading, or
            fraudulent entries.
          </LegalBullet>
          <LegalBullet>
            Do not use the apps on behalf of anyone else unless the company you belong to has
            authorised you to do so.
          </LegalBullet>
        </LegalSection>

        <LegalSection title="Transactions & settlements">
          <p className="text-caption text-text-secondary leading-relaxed">
            Chit-fund operations recorded through the apps — including contributions, payments,
            auction bids, and settlements — are final once confirmed, subject to the rules of the
            applicable chit scheme and applicable law. The records maintained by the company you
            belong to prevail in the event of any discrepancy between those records and your device.
          </p>
        </LegalSection>

        <LegalSection title="Location & security monitoring disclosure">
          <p className="text-caption text-text-secondary leading-relaxed">
            The apps collect your device location periodically — the customer app to prevent fraud
            and abuse, and the agent app to verify field operations — together with MPIN-
            authentication and other security events, as described in the Privacy Policy. By using
            the apps, you acknowledge this location and security monitoring.
          </p>
        </LegalSection>

        <LegalSection title="Suspension & termination">
          <LegalBullet>
            The company you belong to may suspend or terminate your access to the apps at any time,
            including where it reasonably believes there has been a breach of these Terms,
            suspicious or fraudulent activity, or a legal or regulatory requirement to do so.
          </LegalBullet>
          <LegalBullet>You may stop using the apps at any time by uninstalling them.</LegalBullet>
        </LegalSection>

        <LegalSection title="Limitation of liability">
          <p className="text-caption text-text-secondary leading-relaxed">
            To the maximum extent permitted by law, Kraphene and the providers of the apps are not
            liable for any indirect, incidental, or consequential loss, or for loss of profits or
            data, arising from your use of the apps. Nothing in these Terms limits liability that
            cannot be excluded under Indian law.
          </p>
        </LegalSection>

        <LegalSection title="Governing law">
          <p className="text-caption text-text-secondary leading-relaxed">
            These Terms are governed by the laws of India. Any dispute arising out of or in
            connection with these Terms is subject to the courts within the registered jurisdiction
            of the contracting company — the company you belong to.
          </p>
        </LegalSection>

        <LegalSection title="Changes to terms">
          <p className="text-caption text-text-secondary leading-relaxed">
            We may update these Terms from time to time. The revised Terms will be published in the
            apps along with an updated “Last updated” date. Your continued use of the apps after an
            update constitutes your acceptance of the revised Terms.
          </p>
        </LegalSection>

        <LegalSection title="Contact">
          <p className="text-caption text-text-secondary leading-relaxed">
            For questions about these Terms, use the support contact details shown in the apps, or
            contact your relationship manager at the company you belong to.
          </p>
        </LegalSection>

        <div className="border-t border-border pt-4 flex justify-center items-center text-caption text-text-muted">
          Last updated: September 2026
        </div>
      </Stack>
    </AuthLayout>
  );
}
